// Gemeente counselor-performance overzicht. PRD-05 §3.2.

import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { MunicipalityLayout } from "@/components/MunicipalityLayout";

interface CounselorRow {
  id: string;
  label: string;
  activeSince: string;
  lastLoginAt: string | null;
  conceptsApproved: number;
  conceptsRejected: number;
  clientViews: number;
}

interface Response {
  anonymized: boolean;
  counselors: CounselorRow[];
}

export function CounselorsPage() {
  const { t } = useTranslation();

  const query = useQuery({
    queryKey: ["municipality-counselors"],
    queryFn: () => api.get<Response>("/v1/municipality/counselors"),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const errorKey =
    query.error instanceof ApiError ? query.error.i18nKey : "errors.internal";

  const data = query.data;
  const rows = data?.counselors ?? [];

  return (
    <MunicipalityLayout title={t("municipality.counselors.title", "Counselors")}>
      {data?.anonymized && (
        <p className="mb-4 text-xs text-neutral-600 bg-neutral-100 border border-neutral-200 rounded-lg px-3 py-2">
          {t(
            "municipality.counselors.anonymized",
            "Gegevens zijn geanonimiseerd (anonymized)."
          )}
        </p>
      )}

      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        {query.isLoading ? (
          <p className="p-5 text-sm text-neutral-500">{t("loading", "Laden…")}</p>
        ) : query.isError ? (
          <p className="p-5 text-sm text-red-700">{t(errorKey, "Onverwachte fout")}</p>
        ) : rows.length === 0 ? (
          <p className="p-5 text-sm text-neutral-500">
            {t("municipality.counselors.empty", "Geen counselors.")}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600">
              <tr>
                <th className="text-left px-5 py-2 font-medium">
                  {t("municipality.counselors.col.label", "Label")}
                </th>
                <th className="text-left px-5 py-2 font-medium">
                  {t("municipality.counselors.col.activeSince", "Actief sinds")}
                </th>
                <th className="text-left px-5 py-2 font-medium">
                  {t("municipality.counselors.col.lastLogin", "Laatste login")}
                </th>
                <th className="text-center px-3 py-2 font-medium">
                  {t("municipality.counselors.col.approved", "Goedgekeurd")}
                </th>
                <th className="text-center px-3 py-2 font-medium">
                  {t("municipality.counselors.col.rejected", "Afgewezen")}
                </th>
                <th className="text-center px-3 py-2 font-medium">
                  {t("municipality.counselors.col.clientViews", "Client-views")}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                  <td className="px-5 py-2.5 font-medium text-neutral-800">{c.label}</td>
                  <td className="px-5 py-2.5 text-xs text-neutral-600">
                    {new Date(c.activeSince).toLocaleDateString("nl-NL", { dateStyle: "short" })}
                  </td>
                  <td className="px-5 py-2.5 text-xs text-neutral-500">
                    {c.lastLoginAt
                      ? new Date(c.lastLoginAt).toLocaleDateString("nl-NL", {
                          dateStyle: "short",
                        })
                      : t("municipality.counselors.never", "Nog niet")}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <span className="px-1.5 py-0.5 rounded bg-green-50 text-green-800 text-xs font-medium">
                      {c.conceptsApproved}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-800 text-xs font-medium">
                      {c.conceptsRejected}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-center text-xs text-neutral-700">
                    {c.clientViews}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </MunicipalityLayout>
  );
}
